/**
 * Utility functions for handling authentication tokens and secure storage
 */

import { resetUnauthorizedFlag } from "@config/axios/axios.config";
import { POST_AUTH_REDIRECT_STORAGE_KEY } from "@utils/postAuthRedirect";
import type { User } from "@features/Login/types";
import { clearUser, initializeUser } from "@store/slices/userSlice";
import { store } from "@store/store";

const USER_KEY = "user";
const TOKEN_KEY: string = "token";
const USER_AGE_KEY = "userAge";

export const getAuthToken = (): string | null => {
	try {
		return localStorage.getItem(TOKEN_KEY);
	} catch (error) {
		console.error("An error occured while getting token:", error);
		return null;
	}
};

export const setAuthToken = (token: string): void => {
	try {
		localStorage.setItem(TOKEN_KEY, token);
	} catch (error) {
		console.error("An error occured while setting token:", error);
	}
};

export const removeAuthToken = (): void => {
	try {
		localStorage.removeItem(TOKEN_KEY);
		store.dispatch(clearUser());
	} catch (error) {
		console.error("An error occured while removing token:", error);
	}
};

export const getUser = (): User | null => {
	try {
		const user = localStorage.getItem(USER_KEY);
		return user ? JSON.parse(user) : null;
	} catch (error) {
		console.error("An error occured while getting user:", error);
		return null;
	}
};

export const setUser = (user: User): void => {
	try {
		localStorage.setItem(USER_KEY, JSON.stringify(user));
	} catch (error) {
		console.error("An error occured while setting user:", error);
	}
};

export const removeUser = (): void => {
	try {
		localStorage.removeItem(USER_KEY);
	} catch (error) {
		console.error("An error occured while removing user:", error);
	}
};

export const clearAuthData = (): void => {
	removeAuthToken();
	removeUser();
	clearUserAge();
	sessionStorage.clear();
	try {
		localStorage.removeItem(POST_AUTH_REDIRECT_STORAGE_KEY);
	} catch {
		/* ignore */
	}

	// Clear auth-related cookies
	document.cookie.split(";").forEach(cookie => {
		const [name] = cookie.trim().split("=");
		document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
	});
};

// Age utility functions
export const calculateAge = (dateOfBirth: string): number => {
	const today = new Date();
	const birthDate = new Date(dateOfBirth);
	let age = today.getFullYear() - birthDate.getFullYear();
	const monthDiff = today.getMonth() - birthDate.getMonth();
	if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
		age--;
	}
	return age;
};

export const setUserAge = (age: number): void => {
	try {
		localStorage.setItem(USER_AGE_KEY, String(age));
	} catch (error) {
		console.error("An error occured while setting user age:", error);
	}
};

export const getUserAge = (): number | null => {
	try {
		const age = localStorage.getItem(USER_AGE_KEY);
		return age ? parseInt(age, 10) : null;
	} catch (error) {
		console.error("An error occured while getting user age:", error);
		return null;
	}
};

export const clearUserAge = (): void => {
	try {
		localStorage.removeItem(USER_AGE_KEY);
	} catch (error) {
		console.error("An error occured while clearing user age:", error);
	}
};

export const initializeAuthState = (): void => {
	resetUnauthorizedFlag();
	const user = getUser();
	const token = getAuthToken();
	store.dispatch(initializeUser({ user, token }));
};
